import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMeta } from "@/hooks/use-meta";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SITE_NAME = "JobCam Australia";
const SITE_DOMAIN = "https://jobcam.com.au";

interface BlogPostData {
  id: number;
  title: string;
  slug: string;
  content_html: string | null;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  infographic_url: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  tags: string | null;
  faq_schema: { question: string; answer: string }[] | null;
  reading_time: number | null;
  published_at: string | null;
  updated_at: string | null;
}

interface RelatedPost {
  id: number;
  title: string;
  slug: string;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  published_at: string | null;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-AU", { month: "long", day: "numeric", year: "numeric" });
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as BlogPostData | null;
    },
    enabled: !!slug,
  });

  const { data: related } = useQuery({
    queryKey: ["blog-related", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, title, slug, hero_image_url, hero_image_alt, published_at")
        .neq("slug", slug!)
        .order("published_at", { ascending: false })
        .limit(3);
      return (data ?? []) as RelatedPost[];
    },
    enabled: !!slug,
  });

  const metaTitle = post ? `${post.title}` : "Blog";
  const metaDesc = post?.meta_description || "Read our latest articles and insights.";
  useMeta({ title: metaTitle, description: metaDesc, noindex: true });

  if (isLoading) {
    return (
      <div className="container max-w-3xl py-20 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !post) {
    return (
      <div className="container max-w-3xl py-32 text-center space-y-6">
        <h1 className="font-display text-4xl font-bold text-foreground">Article Not Found</h1>
        <p className="text-muted-foreground">The article you're looking for doesn't exist or has been removed.</p>
        <Button asChild variant="outline">
          <Link to="/blog"><ArrowLeft className="h-4 w-4 mr-2" /> Back to Blog</Link>
        </Button>
      </div>
    );
  }

  const faqs = Array.isArray(post.faq_schema) && post.faq_schema.length > 0 ? post.faq_schema : null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    image: post.hero_image_url ? {
      "@type": "ImageObject",
      url: post.hero_image_url,
      caption: post.hero_image_alt || post.title,
      creditText: "JobCam Australia",
      creator: { "@type": "Organization", name: "JobCam Australia" },
      copyrightNotice: "© JobCam Australia",
      acquireLicensePage: `${SITE_DOMAIN}/terms`,
      license: "https://creativecommons.org/licenses/by-nc/4.0/",
    } : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: { "@type": "Organization", name: "Jobcam" },
    publisher: {
      "@type": "Organization",
      name: "JobCam",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_DOMAIN}/jobcam-logo-400.webp`,
        caption: "JobCam logo featuring bold white text on an orange background, representing a construction site camera provider.",
        creditText: "JobCam Australia",
        creator: { "@type": "Organization", name: "JobCam Australia" },
        copyrightNotice: "© JobCam Australia",
        acquireLicensePage: `${SITE_DOMAIN}/terms`,
        license: "https://creativecommons.org/licenses/by-nc/4.0/",
      },
    },
    description: post.meta_description,
    url: `${SITE_DOMAIN}/blog/${post.slug}`,
  };

  const faqSchema = faqs
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}

      <div className="container max-w-3xl pt-8">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to all articles
        </Link>
      </div>

      {post.hero_image_url && (
        <div className="container max-w-4xl mt-6">
          <div className="rounded-xl overflow-hidden" style={{ maxHeight: 400 }}>
            <img
              src={post.hero_image_url}
              alt={post.hero_image_alt || post.title}
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
              width={1200}
              height={400}
              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none"; }}
            />
          </div>
        </div>
      )}

      <article className="container max-w-3xl py-10">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">{post.title}</h1>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-10 flex-wrap">
          {post.published_at && (
            <span className="flex items-center gap-1"><Calendar className="h-4 w-4" /> {formatDate(post.published_at)}</span>
          )}
          {post.reading_time && (
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {post.reading_time} min read</span>
          )}
        </div>

        <div className="blog-prose" dangerouslySetInnerHTML={{ __html: post.content_html || "" }} />

        {post.infographic_url && (
          <div className="mt-10 border border-border rounded-xl overflow-hidden">
            <img
              src={post.infographic_url}
              alt={`${post.title} infographic`}
              className="w-full h-auto"
              loading="lazy"
              width={800}
              height={1200}
              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none"; }}
            />
          </div>
        )}

        {faqs && (
          <section className="mt-16">
            <h2 className="font-display text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible defaultValue="faq-0" className="space-y-3">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="glass-card rounded-lg px-6 border-none">
                  <AccordionTrigger className="text-left text-foreground font-medium hover:text-primary py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {related && related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl font-bold text-foreground mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group glass-card rounded-xl overflow-hidden hover:border-glow transition-all">
                  {r.hero_image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={r.hero_image_url}
                        alt={r.hero_image_alt || r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none"; }}
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-display text-sm font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">{r.title}</h3>
                    {r.published_at && <p className="text-xs text-muted-foreground mt-1">{formatDate(r.published_at)}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
